import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useAuthorizeDealerspace } from '../../../hooks/useIntegrations';
import SoftChip from '../../../components/dashboard/SoftChip';

const AUTHORIZATION_COLORS = { not_required: 'default', pending: 'info', authorized: 'success', refused: 'warning' };
// POST /crm-configs/{id}/dealerspace/authorize/: 422 = code refused/expired or no OAuth credentials, 502 = Followize down.
const AUTHORIZE_ERROR_KEYS = { 403: 'forbidden', 422: 'invalidCode', 502: 'unavailable' };

/**
 * API v3 OAuth of a Dealerspace row (contact update when the lead expires).
 * The technical team builds the authorization URL; an internal user pastes the
 * `code` it redirects to. Everyone else is told to contact the team.
 */
export default function DealerspaceAuthorizationCard({ configId, authorization, isInternal }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const needsAuthorization = authorization === 'pending' || authorization === 'refused';

  const mutation = useAuthorizeDealerspace(configId, {
    onSuccess: () => {
      setCode('');
      setResult({ severity: 'success', message: t('integrations.authorization.success') });
    },
    onError: (error) => {
      const key = AUTHORIZE_ERROR_KEYS[error?.response?.status] ?? 'error';
      setResult({ severity: 'error', message: t(`integrations.authorization.errors.${key}`) });
    },
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    if (code.trim()) mutation.mutate(code.trim());
  };

  return (
    <Card component="form" noValidate onSubmit={handleSubmit}>
      <CardHeader
        title={t('integrations.authorization.title')}
        subheader={t('integrations.authorization.hint')}
        titleTypographyProps={{ variant: 'h6' }}
        action={
          <SoftChip
            sx={{ mt: 1 }}
            color={AUTHORIZATION_COLORS[authorization]}
            label={t(`integrations.authorization.statuses.${authorization}`)}
          />
        }
      />
      <Divider />
      <CardContent>
        <Stack spacing={2.5}>
          <Typography variant="body2" color="text.secondary">
            {t(`integrations.authorization.descriptions.${authorization}`)}
          </Typography>
          {needsAuthorization && !isInternal && (
            <Alert severity="info">{t('integrations.authorization.contactTeam')}</Alert>
          )}
          {needsAuthorization && isInternal && (
            <TextField
              label={t('integrations.authorization.code')}
              helperText={t('integrations.authorization.codeHelper')}
              value={code}
              onChange={(event) => setCode(event.target.value)}
              autoComplete="off"
              fullWidth
            />
          )}
          {result && <Alert severity={result.severity}>{result.message}</Alert>}
        </Stack>
      </CardContent>
      {needsAuthorization && isInternal && (
        <>
          <Divider />
          <CardActions sx={{ px: 3, py: 2, gap: 1, justifyContent: 'flex-end' }}>
            <Button type="submit" variant="contained" disabled={!code.trim() || mutation.isPending}>
              {t('integrations.authorization.submit')}
            </Button>
          </CardActions>
        </>
      )}
    </Card>
  );
}
